import { z } from 'zod';
import { messageReactionSummarySchema, messageReplySchema, reactionEmojiSchema } from './reactions';

/**
 * Contracten van de module messaging: gesprekken tussen medewerkers (Stap 34c, BER-1).
 *
 * Teruggehaald uit de schraplijst (ADR-0050). Het alternatief is niet "geen chat" maar een gesprek
 * op de privé-WhatsApp van een ploegbaas: buiten elk beheer, op zijn privénummer, en niet af te
 * sluiten wanneer iemand vertrekt.
 *
 * **Sinds ADR-0067 bestaan er twee soorten gesprek**: `direct` (twee mensen) en `group` (een
 * ploeg). Punt 1 van ADR-0050 — "geen groepen" — is daarmee overschreven; de reden erachter niet.
 * Een bericht aan de hele fabriek hoort nog steeds in de tijdlijn, met doelgroep, bereik en
 * bevestiging, en niet in een groepsgesprek. Een groep is een ploeg die overlegt, geen omroep.
 *
 * Twee dingen staan hier bewust nog steeds **niet** in, en ze houden het onderscheid met WhatsApp
 * in stand:
 *
 * 1. **Geen aanwezigheid.** Geen "online", geen "aan het typen". Wie een vraag stelt, ziet dat die
 *    verstuurd is en niet of de ander toevallig kijkt.
 * 2. **Geen bedrijfsbrede kanalen.** Een groep heeft leden die iemand heeft toegevoegd; er is geen
 *    gesprek waar iedereen automatisch in zit.
 */

/** `direct` is een gesprek tussen twee mensen; `group` er een tussen drie of meer. */
export const conversationKindSchema = z.enum(['direct', 'group']);
export type ConversationKind = z.infer<typeof conversationKindSchema>;

/**
 * De andere persoon, maar alleen bij een `direct`-gesprek.
 *
 * Bij twaalf deelnemers bestaat "de andere" niet, en een veld dat maar bij één soort klopt is een
 * veld dat ergens in de app als `undefined` opduikt. Daarom hangt de persoon aan een eigen,
 * nulbaar object in plaats van los op de samenvatting.
 */
export const conversationPersonSchema = z.object({
  id: z.guid(),
  displayName: z.string(),
  photoFileId: z.guid().nullable(),
});
export type ConversationPerson = z.infer<typeof conversationPersonSchema>;

export const conversationSummarySchema = z.object({
  id: z.guid(),
  kind: conversationKindSchema,
  /** De groepsnaam, of de naam van de collega. Altijd gevuld: dit is wat in de lijst staat. */
  title: z.string(),
  /** Het icoontje van een groep. `null` bij een `direct`-gesprek, dat een foto toont. */
  icon: z.string().nullable(),
  participantCount: z.number().int(),
  /** Alleen bij `direct`: de andere persoon, voor de foto en de doorklik naar zijn profiel. */
  person: conversationPersonSchema.nullable(),
  /** Het begin van het laatste bericht, voor de lijst. Leeg bij een gesprek zonder berichten. */
  lastMessagePreview: z.string().nullable(),
  lastMessageAt: z.string().datetime().nullable(),
  /**
   * Wie het laatste bericht stuurde, voor het voorvoegsel in de lijst (spec §3.4).
   *
   * De app maakt er "Jij:" of "Lieselot:" van; de server levert de feiten en niet de zin. Dat
   * woordje "Jij" is interfacetekst en hoort vertaald te worden waar de rest van de interface
   * vertaald wordt — niet in het Nederlands uit de API te komen, zoals `audienceDescription` dat
   * wél doet (zie `demo-schuld.md`).
   *
   * Bij een één-op-één-gesprek staat er alleen een voorvoegsel als het bericht van mij is: de naam
   * van de ander staat al bovenaan de rij.
   */
  lastMessageMine: z.boolean(),
  lastMessageSenderFirstName: z.string().nullable(),
  /** Hoeveel berichten ík nog niet gelezen heb. */
  unreadCount: z.number().int(),
  muted: z.boolean(),
  /**
   * Vastgezet: bovenaan mijn lijst, en alleen die van mij.
   *
   * Per persoon en niet per gesprek — wat voor een ploegbaas bovenaan hoort, hoeft dat voor een
   * operator niet te zijn.
   */
  pinned: z.boolean(),
  /**
   * Het bedrijf waarin dit gesprek staat, als dat niet het bedrijf is waarin je nu aangemeld bent
   * (CHT-1, ADR-0096). `null` in je huidige bedrijf. Wie in meer bedrijven werkt, ziet overal
   * dezelfde lijst: de gesprekken van al zijn gekoppelde accounts.
   */
  tenantName: z.string().nullable(),
  /**
   * De gesprekken die in deze rij samenvallen, `id` inbegrepen (CHT-1). Met dezelfde persoon heb
   * je één gesprek, ook als er in twee bedrijven een stond: de rij toont ze samen. Elk van deze ids
   * opent dezelfde rij.
   */
  sourceIds: z.array(z.guid()).min(1),
});
export type ConversationSummary = z.infer<typeof conversationSummarySchema>;

export const conversationListSchema = z.object({
  items: z.array(conversationSummarySchema),
  /** De som over alle gesprekken, voor de badge in de kopbalk. */
  totalUnread: z.number().int(),
});
export type ConversationList = z.infer<typeof conversationListSchema>;

/**
 * Een deelnemer van een gesprek, voor de ledenlijst in het infopaneel.
 *
 * `isCreator` en geen rol: wie een groep maakte mag er leden uit halen, en dat is het enige
 * onderscheid dat deze module kent. Een beheerderskant binnen een groep is een productbeslissing
 * die hier niet voorligt.
 */
export const conversationParticipantSchema = z.object({
  personId: z.guid(),
  displayName: z.string(),
  photoFileId: z.guid().nullable(),
  /**
   * De afdeling, als de collega er een heeft. Komt uit `directory` (ADR-0051).
   *
   * `null` betekent: er is er geen. Dan staat er **niets** — geen streepje en geen "Onbekend".
   * Een plaatshouder voor een gegeven dat niet bestaat, leest als een fout in de gegevens.
   */
  department: z.string().nullable(),
  isCreator: z.boolean(),
  addedAt: z.string().datetime(),
});
export type ConversationParticipant = z.infer<typeof conversationParticipantSchema>;

export const conversationParticipantsSchema = z.object({
  items: z.array(conversationParticipantSchema),
});
export type ConversationParticipants = z.infer<typeof conversationParticipantsSchema>;

/**
 * Een bestand in een bericht.
 *
 * Dezelfde vorm als `postAttachmentSchema`, met één verschil: **geen paginatelling**. Om dezelfde
 * reden als op de berichtkaart (UI-7) — een getal dat soms klopt en soms ontbreekt, leest als een
 * fout in plaats van als een onbekend gegeven.
 */
export const messageAttachmentSchema = z.object({
  fileId: z.guid(),
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int(),
  /** Een afbeelding staat in de ballon; een pdf krijgt een rij met een knop. */
  isImage: z.boolean(),
  hasThumbnail: z.boolean(),
});
export type MessageAttachment = z.infer<typeof messageAttachmentSchema>;

/**
 * Wat er in de stroom van een gesprek staat.
 *
 * `text` is wat iemand zei. `system` is wat er met het gesprek gebeurde — "Sarah heeft Lieselot
 * toegevoegd" — en staat op zijn plaats in de tijd tussen de berichten. Eén soort rij en geen
 * tweede tabel: een tweede tabel zou bij elke pagina samengevoegd en opnieuw gesorteerd moeten
 * worden, en de cursor zou niet meer kloppen.
 *
 * Een systeembericht telt niet mee in de ongelezen-teller en wordt niet de voorbeeldregel in de
 * lijst: het is geen ding dat iemand gezegd heeft.
 */
export const messageKindSchema = z.enum(['text', 'system']);
export type MessageKind = z.infer<typeof messageKindSchema>;

/** Welke gebeurtenis een systeembericht beschrijft. Een sleutel, zodat de app hem vertaalt. */
export const systemEventSchema = z.enum(['participant_added', 'participant_removed']);
export type SystemEvent = z.infer<typeof systemEventSchema>;

export const messageSchema = z.object({
  kind: messageKindSchema,
  id: z.guid(),
  conversationId: z.guid(),
  senderId: z.guid(),
  /** De naam van de afzender. In een groep is "wie zei dit" niet af te leiden uit de kant. */
  senderDisplayName: z.string(),
  body: z.string(),
  createdAt: z.string().datetime(),
  /** Van mij: bepaalt aan welke kant het bericht staat. */
  mine: z.boolean(),
  attachments: z.array(messageAttachmentSchema),
  /**
   * Hoeveel mensen op dit bericht reageerden, en of ik erbij zat.
   *
   * **Geen namen** (ADR-0014): wie reageerde is een lijst mensen, en die hoort hier niet. Het
   * aantal komt uit rijen en staat nergens als teller opgeslagen.
   */
  reactionCount: z.number().int(),
  reactedByMe: z.boolean(),
  /** Per emoji het aantal, meest gekozen eerst. Samen gelijk aan `reactionCount`. */
  reactions: z.array(messageReactionSummarySchema),
  /** Mijn reactie, of `null` als ik niet reageerde. */
  myReaction: reactionEmojiSchema.nullable(),
  /** Het bericht waarop dit een antwoord is, of `null`. */
  replyTo: messageReplySchema.nullable(),
  /**
   * Hoeveel anderen dit bericht lazen — of `null`, en dat is het meestal (ADR-0068).
   *
   * Alleen onder je **eigen** bericht, en alleen in een **groep**. In een gesprek van twee is
   * "gelezen door 1" hetzelfde als "zij heeft het gelezen", en dát is de SLA-suggestie die
   * ADR-0050 wilde vermijden. `null` en geen `0`: er is dan geen aantal, en een nul leest als
   * "niemand heeft het gelezen".
   *
   * Nooit namen (ADR-0014), en nooit een tijdstip. Een aantal, meer niet.
   */
  readByCount: z.number().int().nullable(),
  /**
   * Alleen bij `kind: 'system'`: wat er gebeurde en met wie.
   *
   * De zin wordt in de app gemaakt — `senderDisplayName` is wie het deed, `systemAboutName` over
   * wie het ging. Een kant-en-klare Nederlandse zin uit de server zou een Franstalige lezer
   * Nederlands voorschotelen, dezelfde schuld als bij `audienceDescription`.
   */
  systemEvent: systemEventSchema.nullable(),
  systemAboutName: z.string().nullable(),
});
export type Message = z.infer<typeof messageSchema>;

export const messagePageSchema = z.object({
  items: z.array(messageSchema),
  nextCursor: z.string().nullable(),
});
export type MessagePage = z.infer<typeof messagePageSchema>;

/** Een gesprek met één collega. Idempotent: tweemaal beginnen opent hetzelfde gesprek. */
export const startDirectConversationRequestSchema = z.object({
  personId: z.guid(),
});
export type StartDirectConversationRequest = z.infer<typeof startDirectConversationRequestSchema>;

/**
 * Een groep. Niet idempotent, en met opzet: twee groepen met dezelfde leden zijn twee overleggen
 * over twee onderwerpen, en dat is een geldige wens. Alleen bij één-op-één is een tweede gesprek
 * een fout.
 */
export const startGroupConversationRequestSchema = z.object({
  title: z.string().trim().min(1, 'Een groep heeft een naam nodig.').max(80),
  icon: z.string().trim().min(1).max(40).nullable().optional(),
  /** De leden naast jezelf. Wie de groep maakt, zit er altijd in. */
  participantIds: z
    .array(z.guid())
    .min(1, 'Een groep heeft minstens één ander lid nodig.')
    .max(100),
});
export type StartGroupConversationRequest = z.infer<typeof startGroupConversationRequestSchema>;

/**
 * Eén weg voor beide soorten: `personId` opent een één-op-één-gesprek, `title` + `participantIds`
 * maken een groep. De vorm van het verzoek zegt welke van de twee bedoeld is.
 */
export const startConversationRequestSchema = z.union([
  startDirectConversationRequestSchema,
  startGroupConversationRequestSchema,
]);
export type StartConversationRequest = z.infer<typeof startConversationRequestSchema>;

/**
 * Een gesprek met iemand uit een ander bedrijf, op persoon en niet op bedrijf (CHT-1, ADR-0096).
 *
 * Het paar (bedrijf, persoon) zegt wie je bedoelt, zoals de contactenlijst over bedrijven heen het
 * toont. Het gesprek komt in een bedrijf dat jullie allebei delen, of de server geeft het gesprek
 * terug dat er met die persoon al is, in welk bedrijf ook.
 */
export const startConversationWithRequestSchema = z.object({
  tenantId: z.guid(),
  personId: z.guid(),
});
export type StartConversationWithRequest = z.infer<typeof startConversationWithRequestSchema>;

/**
 * De bestanden die in een gesprek gedeeld zijn, nieuwste eerst (spec §5.2).
 *
 * Dezelfde vorm als een bijlage in een bericht, met het tijdstip erbij: in het infopaneel is
 * "wanneer" de enige manier om twee versies van dezelfde werkinstructie uit elkaar te houden.
 */
export const sharedFileSchema = messageAttachmentSchema.extend({
  messageId: z.guid(),
  sharedAt: z.string().datetime(),
});
export type SharedFile = z.infer<typeof sharedFileSchema>;

export const sharedFilesSchema = z.object({ items: z.array(sharedFileSchema) });
export type SharedFiles = z.infer<typeof sharedFilesSchema>;

export const addParticipantsRequestSchema = z.object({
  personIds: z.array(z.guid()).min(1).max(100),
});
export type AddParticipantsRequest = z.infer<typeof addParticipantsRequestSchema>;

export const sendMessageRequestSchema = z.object({
  body: z.string().trim().min(1, 'Een bericht mag niet leeg zijn.').max(2000),
  /**
   * Bestanden bij `media`, in de volgorde waarin ze getoond worden.
   *
   * Tien is dezelfde grens als bij een bericht op de tijdlijn. Staat `media` uit, dan wordt een
   * poging tot bijlage netjes geweigerd en blijft tekst gewoon werken.
   */
  attachmentFileIds: z.array(z.guid()).max(10).default([]),
  /** Antwoorden op een bericht uit hetzelfde gesprek (BER-8). */
  replyToId: z.guid().nullable().optional(),
});
export type SendMessageRequest = z.infer<typeof sendMessageRequestSchema>;

/**
 * Reageren of de reactie weghalen. `emoji` kiest welke; zonder is het een duim, zoals voorheen.
 * Een andere emoji sturen vervangt je reactie.
 */
export const setReactionRequestSchema = z.object({
  reacted: z.boolean(),
  emoji: reactionEmojiSchema.default('thumbs_up'),
});
export type SetReactionRequest = z.infer<typeof setReactionRequestSchema>;

export const muteConversationRequestSchema = z.object({
  muted: z.boolean(),
});
export type MuteConversationRequest = z.infer<typeof muteConversationRequestSchema>;

export const pinConversationRequestSchema = z.object({
  pinned: z.boolean(),
});
export type PinConversationRequest = z.infer<typeof pinConversationRequestSchema>;

export * from './cross-tenant';
export * from './reactions';
